import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { isServer, URL_GAME_CHAT } from 'out-game/constants'
import { ErrorEnum, getErrorText } from '../../util/util'
import Chat from '../Chat/Chat'
import Input from '../Input/Input'
import { URL_GENERAL_CHAT } from '../../agent/constants'
import useSocket from '../../hooks/useSocket/useSocket'
import { StateType } from '../../hooks/useSocket/useSocket.types'
import { MainWindowProps } from './ChatWindow.types'
import './ChatWindow.scss'
import { ChatViewMode } from '../Chat/Chat.types'
import cn from 'classnames'
import { Alert, Typography } from '../../../out-game/components'
import { Status } from '../../../out-game/components/Alert'

import Draggable from 'react-draggable'
import { useResize } from '../../hooks/useSocket/useResize'
import { useTranslationStrict } from '../../../out-game/hooks'

// TODO: Move this constant to process.env
const URL_BASE = 'wss://noti.dev.out-game.com'

const MainWindow: FC<MainWindowProps> = ({
     setChatState,
     setWsGeneralChat,
     wsGeneralChat,
     setIsNotificationsEnabled,
     setIsUserConnected,
     isNotificationsEnabled,
     isUserConnected,
     user,
     isChatOpen,
     setIsChatOpenedOuter,
     setUnreadMessages,
     isSubscriptionExist,
     title,
     gameSlug,
     viewMode = ChatViewMode.small,
     setIsNotificationsEnabledGameChat,
     isNotificationsEnabledGameChat,
     setUnreadMessagesGameChat,
     isUnreadMessagesGameChat
   }) => {
  const { t } = useTranslationStrict()

  const chatListRef = useRef<HTMLUListElement>(null)

  const isNotificationsEnabledLocal = gameSlug && isNotificationsEnabledGameChat ? 
    (isNotificationsEnabledGameChat[gameSlug] || false) 
    : isNotificationsEnabled
 
  const setIsNotificationsEnabledLocal = (value: boolean) => {
    if (gameSlug && setIsNotificationsEnabledGameChat) {
      const newState: Record<string, boolean> = {...isNotificationsEnabledGameChat} 
      newState[gameSlug] = value
      setIsNotificationsEnabledGameChat(newState)
      return
    }
    setIsNotificationsEnabled(value)
  }

  const setUnreadMessagesLocal = (value: boolean) => {
    if (gameSlug && setUnreadMessagesGameChat) {
      const newState: Record<string, boolean> = {...isUnreadMessagesGameChat}
      newState[gameSlug] = value
      setUnreadMessagesGameChat(newState)
      return
    }
    setUnreadMessages(value)
  }

  const { chatState, chatActions } = useSocket(
    gameSlug ? `${URL_GAME_CHAT || URL_BASE}/${gameSlug}` : URL_GENERAL_CHAT,
    user,
    setWsGeneralChat,
    wsGeneralChat,
    setIsNotificationsEnabledLocal,
    setIsUserConnected,
    isUserConnected,
    setUnreadMessagesLocal,
    isChatOpen,
    isNotificationsEnabledLocal,
  )
  const [errorLocal, setErrorLocal] = useState('')
  const [_viewMode, setViewMode] = useState<ChatViewMode>(viewMode)
  const [pageWidth, setPageWidth] = useState(document.documentElement.scrollWidth)
  const parentGameChat = useRef<Element | null>(null)
  const [offsetTopGameChat, setOffsetTopGameChat] = useState(0)
  const [disabledDraggable, setDisabledDraggable] = useState(false)
  const [disabledInput, setDisabledInput] = useState(false)
  const chatWrapperRef = useRef<HTMLDivElement | null>(null)
  const chatHeight = chatWrapperRef.current ? chatWrapperRef.current.getBoundingClientRect().height : 0

  useEffect(() => {
      const parentGameChatElement = document.querySelector('.game-page__categories')
      parentGameChat.current = parentGameChatElement

  }, [parentGameChat])

  useEffect(() => {
    setOffsetTopGameChat(chatHeight ? window.innerHeight - chatHeight - 103 : 0)
  }, [chatHeight])


  const getError = (
    error: ErrorEnum | string,
    isBlocked: boolean,
    errorLocal: string,
  ) => {
    let textError = ''
    if (error) {
      if (
        error === 'user_has_not_joined_the_chat' &&
        !isSubscriptionExist &&
        user
      ) {
        textError = getErrorText('no_active_subscription', t)
      } else {
        textError = getErrorText(error, t)
      }

      return {
        text: textError,
        status: Status.error,
        onClose: () => {
          chatActions.setErrorMessage('')
        },
      }
    }
    if (isBlocked) {
      return {
        text: 'Пардон! Вас заблокировали, так как Вы нарушили правила пользования общим чатом.',
        status: Status.warning,
        onClose: () => {
          chatActions.setUserBlocked(false)
        },
      }
    }
    if (errorLocal) {
      return {
        text: errorLocal,
        status: Status.warning,
        onClose: () => {
          setErrorLocal('')
        },
      }
    }
    return null
  }

  useEffect(() => {
    if (chatListRef.current) {
      chatListRef.current.scrollTop = chatListRef.current.scrollHeight
    }

    if (!chatState || !setChatState) return
    setChatState((chatState as StateType) || null)
  }, [chatState.messagesList, chatState.usersList, isUserConnected, setChatState])

  const errorObject = getError(
    chatState.error,
    chatState.isUserBlocked,
    errorLocal,
  )

  const onClickSend = (value: string) => {
    if (value.trim()) {
      chatActions.sendCommand({ action: 'send_message', data: { text: value } })
    }
  }

  const onChangeSettings = (value: boolean) => {
    chatActions.sendCommand({
      action: 'update_chat_settings',
      data: { is_notifications_enabled: value },
    })
  }

  const onJoinChat = () => {
    if (user) {
      chatActions.sendCommand({ action: 'join_the_chat' })
      setUnreadMessagesLocal(false)
      setIsUserConnected(true)

      if (!isSubscriptionExist) {
        setDisabledInput(true)
      }
    } else {
      setErrorLocal(t('chat:errors.youNeedToBeLogged'))
      setDisabledInput(true)
    }
  }

  const onLeftChat = () => {
    if (user) {
      chatActions.sendCommand({ action: 'leave_chat' })
      setIsUserConnected(false)
    }
  }

  const onToggleChat = () => {
    if (isUserConnected && isChatOpen) {
      onLeftChat()
    }
    if (!isUserConnected && !isChatOpen) {
      onJoinChat()
    }
    if (setIsChatOpenedOuter) {
      setIsChatOpenedOuter(!isChatOpen)
    }
  }

  const mouseListener = useCallback(
    (evt) => {
      const currentNode = evt.target
      const parent1 = currentNode?.parentNode
      const parent2 = parent1?.parentNode
      const parent3 = parent2?.parentNode
      const parent4 = parent3?.parentNode

      if (
        !(
          (currentNode &&
            currentNode.classList &&
            currentNode.classList.contains('js-general-chat')) ||
          (parent1 &&
            parent1.classList &&
            parent1.classList.contains('js-general-chat')) ||
          (parent2 &&
            parent2.classList &&
            parent2.classList.contains('js-general-chat')) ||
          (parent3 &&
            parent3.classList &&
            parent3.classList.contains('js-general-chat')) ||
          (parent4 &&
            parent4.classList &&
            parent4.classList.contains('js-general-chat')) ||
          (parent1 &&
            parent1.classList &&
            parent1.classList.contains('emoji')) ||
          (parent2 &&
            parent2.classList &&
            parent2.classList.contains('emoji')) ||
          (parent3 &&
            parent3.classList &&
            parent3.classList.contains('emoji')) ||
          (parent4 && parent4.classList && parent4.classList.contains('emoji'))
        ) &&
        !isChatOpen &&
        isUserConnected
      ) {
        chatActions.sendCommand({ action: 'leave_chat' })
        setIsUserConnected(false)
      }
    },
    [chatActions, isChatOpen],
  )

  useEffect(() => {
    if (!isServer) {
      document.addEventListener('click', mouseListener)
      return () => {
        document.removeEventListener('click', mouseListener)
      }
    }
  }, [mouseListener])

  useEffect(() => {
    if (isChatOpen && !isUserConnected) {
      onJoinChat()
    }
  }, [isChatOpen])

  useResize(() => {
    setPageWidth(document.documentElement.scrollWidth)
    setOffsetTopGameChat(chatHeight ? window.innerHeight - chatHeight - 103 : 0)
  })

  useEffect(() => {
    if (_viewMode === ChatViewMode.full) {
      setDisabledDraggable(true)
    } else {
      setDisabledDraggable(false)
    }
  }, [_viewMode])

  if (!isChatOpen) {
    return null
  }

  const ChatWrapperClass = cn('chat-wrapper', {
    'chat-wrapper_full': _viewMode === ChatViewMode.full,
    'chat-wrapper_small': _viewMode === ChatViewMode.small,
    'chat-wrapper_game': !!gameSlug,
  })

  const ChatWrapperInnerClass = cn('chat-wrapper__inner', {
    'chat-wrapper__inner_full': _viewMode === ChatViewMode.full,
    'chat-wrapper__inner_small': _viewMode === ChatViewMode.small,
    'chat-wrapper__inner_game': !!gameSlug,
  })

  const getPosition = (disabledDraggable: boolean, isGameChat: boolean, isFullMode: boolean) => {
    if (disabledDraggable && !isFullMode) {
      if (isGameChat) {
        return { x: -30, y: -offsetTopGameChat };
      }
      return ({ x: 0, y: 0 });
    }

    if (isFullMode) {
      if (isGameChat) {
        return { x: -30, y: 0 };
      }
      return ({ x: 0, y: 0 });
    }

    return undefined;
  }

 return (
    <>
      <Draggable
        handle='#draggableChatHeader'
        bounds={!!gameSlug ?
          { top: -offsetTopGameChat, left: 0, right: pageWidth - 480 - 60 } :
          { top: -offsetTopGameChat, left: 0, right: pageWidth - 480 - 45 }}
        disabled={disabledDraggable}
        position={getPosition(disabledDraggable, !!gameSlug, !_viewMode)}
        defaultPosition={!!gameSlug ? { x: 0, y: 0 } : undefined}
      >
        <div className={ChatWrapperClass} ref={chatWrapperRef}>
          <div
            className={ChatWrapperInnerClass}>
            <Chat
              listMessages={chatState.messagesList}
              isNotificationsEnabled={isNotificationsEnabledLocal}
              onChangeSettings={onChangeSettings}
              user={user}
              isUserConnected={isUserConnected}
              chatListRef={chatListRef}
              onToggleChat={onToggleChat}
              isChatOpened={isChatOpen}
              title={title}
              isGameChat={!!gameSlug}
              viewMode={_viewMode}
              changeViewMode={setViewMode}
            />
            <Input
              onClickSend={onClickSend}
              onJoinChat={onJoinChat}
              isUserConnected={isUserConnected}
              disabled={disabledInput}
            />
          </div>
        </div>
      </Draggable>
      <Alert
        open={!!errorObject}
        status={errorObject ? errorObject.status : Status.error}
        onClose={errorObject?.onClose}
      >
        <Typography size={22} lineHeight={1} color='Emphasized' className='alert-restyled__text'>
          {errorObject?.text}
        </Typography>
      </Alert>
    </>
  )
}

export default MainWindow
